import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Cursor from "./Cursor";

const NAV_ITEMS = [
  { to: "/work", label: "Work", testid: "nav-work" },
  { to: "/build", label: "The Build", testid: "nav-build" },
  { to: "/apply", label: "Apply", testid: "nav-apply" },
];

function NavBar() {
  const location = useLocation();
  const linksRef = useRef(null);
  const [indicator, setIndicator] = useState({ left: 0, width: 0, visible: false });
  const [mobileOpen, setMobileOpen] = useState(false);

  useLayoutEffect(() => {
    const root = linksRef.current;
    if (!root) return;
    const active = root.querySelector(".nav-link.is-active");
    if (active) {
      const r = active.getBoundingClientRect();
      const parentR = root.getBoundingClientRect();
      setIndicator({ left: r.left - parentR.left, width: r.width, visible: true });
    } else {
      setIndicator((s) => ({ ...s, visible: false }));
    }
  }, [location.pathname]);

  // Reposition on resize
  useEffect(() => {
    const onResize = () => {
      const root = linksRef.current;
      if (!root) return;
      const active = root.querySelector(".nav-link.is-active");
      if (active) {
        const r = active.getBoundingClientRect();
        const parentR = root.getBoundingClientRect();
        setIndicator({ left: r.left - parentR.left, width: r.width, visible: true });
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lock body scroll while mobile menu open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-bg/80 backdrop-blur-md hairline-b" data-testid="site-header">
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
          <img src="/mark.svg" alt="Socialize Video mark" className="h-6 w-6" />
          <span className="font-display font-bold text-[1.05rem] tracking-tight text-ink">
            Socialize <span className="font-normal opacity-80">Video</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
          <div ref={linksRef} className="relative flex items-center gap-9">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) => `nav-link ${isActive ? "is-active" : ""}`}
              >
                {item.label}
              </NavLink>
            ))}
            <span
              className="nav-indicator"
              style={{
                left: indicator.left,
                width: indicator.width,
                opacity: indicator.visible ? 1 : 0,
              }}
              aria-hidden="true"
            />
          </div>
          <Link to="/apply" className="cta-outline" data-testid="nav-apply-cta">Apply Now</Link>
        </nav>

        {/* Mobile toggle */}
        <button
          type="button"
          className="md:hidden text-ink p-2 -mr-2"
          aria-label="Open menu"
          onClick={() => setMobileOpen(true)}
          data-testid="mobile-menu-open"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] bg-bg" data-testid="mobile-menu-overlay">
          <div className="container-x flex items-center justify-between h-16 md:h-20">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5">
              <img src="/mark.svg" alt="" className="h-6 w-6" />
              <span className="font-display font-bold text-[1.05rem] tracking-tight text-ink">
                Socialize <span className="font-normal opacity-80">Video</span>
              </span>
            </Link>
            <button
              type="button"
              className="text-ink p-2 -mr-2"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              data-testid="mobile-menu-close"
            >
              <X size={22} />
            </button>
          </div>
          <div className="container-x mt-16 flex flex-col gap-8">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setMobileOpen(false)}
                className="font-display text-4xl font-bold tracking-tight text-ink hover:text-teal transition-colors"
                data-testid={`${item.testid}-mobile`}
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/apply"
              onClick={() => setMobileOpen(false)}
              className="cta mt-6 self-start"
              data-testid="mobile-apply-cta"
            >
              Apply Now <span aria-hidden="true">→</span>
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="section-dark hairline-t mt-32" data-testid="site-footer">
      <div className="container-x py-10 md:py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/mark.svg" alt="" className="h-6 w-6" />
          <span className="font-display font-bold text-[1rem] tracking-tight text-ink">
            Socialize <span className="font-normal opacity-80">Video</span>
          </span>
        </Link>
        <nav className="flex flex-wrap items-center gap-7 text-sm text-muted">
          <Link to="/work" className="hover:text-ink transition-colors" data-testid="footer-work">Work</Link>
          <Link to="/build" className="hover:text-ink transition-colors" data-testid="footer-build">The Build</Link>
          <Link to="/apply" className="hover:text-ink transition-colors" data-testid="footer-apply">Apply</Link>
        </nav>
        <p className="text-xs text-muted">© 2026 Socialize Video LLC. Delaware.</p>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <>
      <Cursor />
      <NavBar />
      <main className="pt-16 md:pt-20">
        {children}
      </main>
      <Footer />
    </>
  );
}
